import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon, ClockIcon, ShoppingBasketIcon, UsersIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { ButtonLink } from '../ui/Button';
import { GROCERY_LABEL } from '../../utils/pricing';
import { currency } from '../../utils/format';
import { equipmentLabel } from '../../data/taxonomy';
import { useLocale } from '../../contexts/LocaleContext';
import { spring } from '../../utils/motion';
import { cn } from '../../utils/cn';
import type { ChefMenu } from '../../types/chef';
export function MenuCard({
  menu,
  chefSlug,
  defaultOpen = false
}: {
  menu: ChefMenu;
  chefSlug: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const reduce = useReducedMotion();
  const {
    locale,
    currencyCode
  } = useLocale();
  const deliveryOnly = !menu.fulfillment.includes('in_home');
  const price = deliveryOnly ? menu.pricePerPortion ?? menu.pricePerGuest : menu.pricePerGuest;
  return <article className={cn('overflow-hidden rounded-xl border bg-surface', menu.signature ? 'border-accent' : 'border-hairline')} data-id="element-358" data-mp="3axo39ggpcz9h">
      <div className="flex flex-col gap-4 p-5 sm:flex-row" data-id="element-359" data-mp="3kpal3vnjpwl7">
        <img src={menu.image} alt={menu.courses[0]?.description ?? menu.name} className="h-28 w-full shrink-0 rounded-lg object-cover sm:w-40" loading="lazy" data-id="element-360" data-mp="3kosf47b30403" />
        <div className="min-w-0 flex-1" data-id="element-361" data-mp="2s2zd3zxm0ted">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-362" data-mp="2n2bp7n4ogsod">
            <div data-id="element-363" data-mp="31z2kplb8dk8p">
              {menu.signature && <Badge tone="accent" className="mb-2" data-id="element-364" data-mp="320ngtfd71b8o">
                  Signature
                </Badge>}
              <h3 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink" data-id="element-365" data-mp="2x22e99agyfh2">{menu.name}</h3>
              <p className="mt-1 max-w-[52ch] text-sm text-ink-2" data-id="element-366" data-mp="haoafabsra86">{menu.tagline}</p>
            </div>
            <p className="text-right" data-id="element-367" data-mp="jcytlytjushg">
              <span className="tnum font-display text-xl font-semibold text-ink" data-id="element-368" data-mp="jbx5otrfqbib">{currency(price, locale, currencyCode)}</span>
              <span className="block text-[13px] text-ink-2" data-id="element-369" data-mp="oagvj595g7fy">per {deliveryOnly ? 'portion' : 'guest'}</span>
            </p>
          </div>

          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-ink-2" data-id="element-370" data-mp="ya7r7a9g4shm">
            <li className="flex items-center gap-1.5" data-id="element-371" data-mp="y7v6ipcb040v">
              <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-372" data-mp="ns4nu25x4n7" /> min {menu.minGuests}
            </li>
            {!deliveryOnly && <li className="flex items-center gap-1.5" data-id="element-373" data-mp="5j7lmuvgvgnu">
                <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-374" data-mp="ajv9j7oefhdu" /> {menu.includedHours} hrs included
              </li>}
            <li className="flex items-center gap-1.5" data-id="element-375" data-mp="fd6jhhoice9z">
              <ShoppingBasketIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-376" data-mp="3e0kfosk2kp2z" /> {GROCERY_LABEL[menu.groceries]}
              {menu.groceries === 'estimate' && menu.groceryEstimatePerGuest ? ` (~${currency(menu.groceryEstimatePerGuest, locale, currencyCode)}/guest)` : ''}
            </li>
            <li data-id="element-377" data-mp="1w4ido3gnj1x8">{menu.leadTimeDays} days notice</li>
          </ul>

          {!!menu.dietaryTags.length && <ul className="mt-3 flex flex-wrap gap-1.5" data-id="element-378" data-mp="1r6qm5ajx5htk">
              {menu.dietaryTags.map(tag => <li key={tag} className="rounded-full bg-oat px-2.5 py-1 text-[12px] text-ink-2" data-id="element-379" data-mp="1m7x7vfwt5msc">
                  {tag}
                </li>)}
            </ul>}

          <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-380" data-mp="1cdn4z4a8p63d">
            <ButtonLink to={`/request/${chefSlug}?menu=${menu.id}`} variant="outline" size="sm" data-id="element-381" data-mp="18exupkki04ny">
              Request this menu
            </ButtonLink>
            <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary" data-id="element-382" data-mp="yfytuo5z5dbi">
              {open ? 'Hide' : 'See'} the {menu.courses.length} courses
              <ChevronDownIcon className={cn('h-4 w-4 transition-transform duration-200 ease-out', open && 'rotate-180')} aria-hidden="true" data-id="element-383" data-mp="2yhyemqfovcpt" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-384" data-mp="2oqu0tu1r1xwk">
        {open && <motion.div initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6
      }} animate={reduce ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -4
      }} transition={reduce ? {
        duration: 0.14
      } : spring.snappy} className="border-t border-hairline bg-oat px-5 py-4" data-id="element-385" data-mp="2asduw5bpobfh">
            <ol className="space-y-2.5" data-id="element-386" data-mp="3v8h3jx5ucrkb">
              {menu.courses.map((course, i) => <li key={course.name} className="flex gap-3 text-sm" data-id="element-387" data-mp="427c3mqjoaiq" data-mpi={i}>
                  <span className="tnum w-5 shrink-0 font-display font-semibold text-action" data-id="element-388" data-mp="41edxa5nitc7">{i + 1}</span>
                  <span data-id="element-389" data-mp="1oi5f97pue0a0">
                    <span className="font-medium text-ink" data-id="element-390" data-mp="1jl562nxp4heo">{course.name}</span>
                    <span className="block text-ink-2" data-id="element-391" data-mp="1jj2c5stfa44o">{course.description}</span>
                  </span>
                </li>)}
            </ol>
            {!!menu.equipmentNeeded.length && <p className="mt-4 border-t border-hairline pt-3 text-[13px] text-ink-2" data-id="element-392" data-mp="1thi05mx5kx9y">
                <span className="font-medium text-ink" data-id="element-393" data-mp="27cssask0j4am">Needs from your kitchen: </span>
                {menu.equipmentNeeded.map(id => equipmentLabel(id)).join(', ')}.
              </p>}
          </motion.div>}
      </AnimatePresence>
    </article>;
}